import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ResidueType} from 'biojs/BioConstants';
import {formatPolymerResidueCount} from 'biojs/Util';
import PolymerPreview from 'molbio/components/visualizations/PolymerPreview';
import {VisualHistoryEventListItemFragment} from 'molbio/visual-history/__generated/VisualHistoryEventListItemFragment';
import {convertPolymerMapNodeToVisualSequence} from 'molbio/visual-history/utils/VisualSequenceConverters';
import VisualHistoryEventHeader from 'molbio/visual-history/VisualHistoryEventHeader';
import VisualHistoryEventIcon from 'molbio/visual-history/VisualHistoryEventIcon';
import VisualHistoryEventMetadataFieldList from 'molbio/visual-history/VisualHistoryEventMetadataFieldList';
import VisualHistoryEventSource from 'molbio/visual-history/VisualHistoryEventSource';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY} from 'ui-library/Colors';

type VisualHistoryPolymerMapNode = VisualHistoryEventListItemFragment['eventVisualization'];

export const VISUAL_HISTORY_EVENT_LIST_ITEM_FRAGMENT = gql`
  fragment VisualHistoryEventListItemFragment on VisualHistoryEventNode {
    eventType
    message
    createdAt
    resultingEntityVersion {
      entityId
      entityType
      entityVersionId
    }
    user {
      ... on UserNode {
        id
        name
      }
      ... on ServiceAccountActorNode {
        id
        name
      }
      ... on BenchlingAppNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
    metadataFields {
      ...VisualHistoryEventMetadataFieldFragment
    }
    sourceInfo {
      ...VisualHistoryEventSourceInfoFragment
    }
    eventVisualization {
      ...VisualHistoryPolymerMapFragment
    }
  }
`;

/**
 * Render the visualization for a visual history event.
 *
 * Note: This component is currently polymer-specific and only renders polymer maps. In the future we may
 *   need to support other kinds of visualizations for different types of events/objects.
 * @param eventVisualization Event visualization information.
 * @param index Event index.
 * @param resultingEntityType Entity type produced by the event.
 * @constructor
 */
export function VisualHistoryEventPreview({
  eventVisualization,
  index,
  resultingEntityType,
}: {
  eventVisualization: VisualHistoryPolymerMapNode;
  index: number;
  resultingEntityType: string;
}): JSX.Element {
  const residueType = resultingEntityType === 'protein' ? ResidueType.AA : ResidueType.NUCLEOTIDE;
  return (
    <div
      className={css(styles.previewContainer)}
      {...getTestProps({component: 'VisualHistoryEventPreview', element: 'root'})}
    >
      <p
        className={css(styles.residueLengthDisplay)}
        {...getTestProps({component: 'VisualHistoryEventPreview', element: 'residueLength'})}
      >
        {formatPolymerResidueCount(eventVisualization.length, residueType)}
      </p>
      <div className={css(styles.polymerPreview)}>
        <PolymerPreview
          polymer={convertPolymerMapNodeToVisualSequence(eventVisualization)}
          uniqueIdPrefix={index.toString()}
        />
      </div>
    </div>
  );
}

export interface VisualHistoryEventListItemProps {
  event: VisualHistoryEventListItemFragment;
  onClickCreatedAt: ((entityVersionId: string) => void) | null;
  index?: number;
  showAsStandalone?: boolean;
}

/**
 * Render a single visual history event, which includes a header, a polymer map, source info and metadata.
 * @param event Visual history event data.
 * @param onClickCreatedAt Callback invoked when a user clicks on the created at timestamp.
 * @param showAsStandalone Whether the event is rendered as a standalone item instead of part of a list.
 * Temporary prop to be removed after moving from assembly history to full visual history.
 * @constructor
 */
export default function VisualHistoryEventListItem({
  event,
  onClickCreatedAt,
  showAsStandalone = false,
  index,
}: VisualHistoryEventListItemProps): JSX.Element {
  return (
    <div
      className={classNames(css(styles.eventItem), {[css(styles.listedEventItem)]: !showAsStandalone})}
      {...getTestProps({component: 'VisualHistoryEventListItem', element: 'root'})}
    >
      <VisualHistoryEventIcon eventType={event.eventType} />
      <div className={css(styles.eventData)}>
        <VisualHistoryEventHeader
          headerInfo={{user: event.user, message: event.message, createdAt: event.createdAt}}
          onClickCreatedAt={
            onClickCreatedAt
              ? () => {
                  onClickCreatedAt(event.resultingEntityVersion.entityVersionId);
                }
              : null
          }
        />
        <VisualHistoryEventMetadataFieldList fields={event.metadataFields} />
        {event.sourceInfo && <VisualHistoryEventSource sourceInfo={event.sourceInfo} />}
      </div>
      <VisualHistoryEventPreview
        eventVisualization={event.eventVisualization}
        index={index ?? 0}
        resultingEntityType={event.resultingEntityVersion.entityType}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  eventItem: {
    display: 'flex',
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    marginBottom: '16px',
    position: 'relative',
    minWidth: 'fit-content',
  },
  listedEventItem: {
    borderLeft: 'none',
    borderRadius: '0 4px 4px 0',
  },
  eventData: {
    display: 'grid',
    gridAutoRows: 'min-content',
    gap: '16px',
    borderRight: `1px solid ${GRAY}`,
    paddingTop: '13px',
    paddingLeft: '24px',
    minWidth: '375px',
    flex: 1,
  },
  previewContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '12px 0',
    flex: 1,
  },
  residueLengthDisplay: {
    position: 'absolute',
    right: '8px',
    top: '8px',
  },
  polymerMapContainer: {
    flex: 1,
  },
  polymerPreview: {
    minWidth: '200px',
    maxWidth: '300px',
  },
});
